/**
 * /zh/product-brief-template — ProductBriefTemplate.tsx 的中文版,
 * 命中「产品简报模板 / 产品 brief 模板 / 一页纸产品简报 / product
 * brief 模板」搜索意图。
 *
 * 差异化角度:产品简报(product brief)是比 PRD 更短的一页纸文档,
 * 用在想法早期、还便宜改主意的时候做对齐。本页直接把简报结构
 * (七个板块,逐条写清)给搜索者,让他拿到能直接用的真东西;
 * 再讲真正的问题:简报只在写完那一周成立,之后 PRD 和决策不断
 * 往前走,简报却被冻在原地,慢慢和下游脱节。Draftlize 让简报里的
 * 决策变成卡片、和下游 PRD 连起来,依赖一变就自动标 stale。
 *
 * 中文 + inline(无 i18n),对齐 DecisionLogTemplateZh.tsx 与各对比页。
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ProductBriefTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/product-brief-template"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="产品简报模板:一页纸,始终最新 · Draftlize"
      documentDescription="一份可直接复制的产品简报模板 —— 问题、目标用户、为何是现在、初步方案、成功指标、范围与非目标、待解问题。一页纸的简报最适合想法早期对齐,但它的麻烦在于:写完那一周成立,之后 PRD 和决策不断往前走,它却被冻在原地。Draftlize 让简报里的决策变成卡片、和下游 PRD 连起来,依赖一变就自动标 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 产品简报</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份始终一页、<br/>
            <em>且一直成立的产品简报。</em>
          </h1>
          <p className="lede">
            你想找的是一份<em>产品简报模板</em>:一页纸、几个板块,在动手写<a href="/zh/product-requirements-document" style={{ color: 'var(--amber)' }}>完整 PRD</a>之前先贴进去对齐。它就在下面,直接拿走。一份好的简报,是团队能买到的最便宜的对齐 —— 因为它短到一眼读完,也早到还能随便改主意。但有件事没人提醒你:一份简报通常只在写完那一周成立。PRD 越长越细、决策一个个落下来,这份本该当事实源的一页纸,反倒成了第一个过时的东西。Draftlize 让简报始终保持一页 —— 同时把里面的决策变成卡片、和下游的 PRD 连起来,只要某个依赖一变,它就自己标成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板本体</span>
            <h2 className="serif">七个板块。<em>一页纸,随处可贴。</em></h2>
          </div>
          <p>这就是整份产品简报。它故意只占一页 —— 一份需要你滚动才能读完的简报,不过是换了个小名字的 PRD。在文档、wiki 或 markdown 里都能用,适合想法早期对齐,在它值得一份完整 spec 之前。再往下那一节,讲的是当简报不再是冻住的文字、而是能自己保持最新时,会有什么不同。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '问题',
              hint: '从这里开始',
              body: '用一两句大白话说清痛点 —— 谁难受、多频繁。不是套了问题外壳的方案。如果你不提自己那个功能就说不清问题,那你写的不是简报,是一个在找借口的方案。',
            },
            {
              k: '目标用户',
              hint: '为谁做',
              body: '这件事具体是给谁的。「用 AI agent 一个人发版的独立开发者」,而不是「用户」。一份给所有人的简报等于给不了任何人;这一栏越窄,后面每一个取舍都越好做。',
            },
            {
              k: '为何是现在',
              hint: '时机',
              body: '是什么变了,让这件事值得这个季度做、而不是去年做 —— 一个新出现的能力、一次市场的转向、一项终于降下来的成本。这一栏,把真正的赌注和「将来再说」的清单项区分开。',
            },
            {
              k: '初步方案',
              hint: '大致形状',
              body: '用一段话讲清思路,不是一份 spec。足够让读者脑子里浮现出和你一样的画面,又足够模糊 —— 在你还没确认它值得做之前,别把每个细节都提前定死。',
            },
            {
              k: '成功指标',
              hint: '怎么算成了',
              body: '一个能告诉你「这事成了」的数字,带上目标值。「第一周有 40% 的新项目建了简报」。说不出这个指标,你就分不清「发了」和「成了」。',
            },
            {
              k: '范围与非目标',
              hint: '边界',
              body: 'v1 做什么 —— 更要紧的是,你刻意不做什么。简报的价值很大一块在非目标上:它能在那些「顺手把它也做了吧」的对话开口之前,就把它们摁住。',
            },
            {
              k: '待解问题',
              hint: '未知数',
              body: '还没拍板的决策,老老实实列出来。一份假装什么都定了的简报是在撒谎。这些条目,正是日后会升级成真正决策、并继续往下进 PRD 的那些。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          这就是这份简报。往下:为什么写完那一周它就开始过时 —— 以及我们怎么解决。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么一份产品简报<em>会变陈旧。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它是第一周的一张快照</h3>
            <p>简报捕捉的是想法最早、最模糊的那个瞬间 —— 这恰恰是它的本职。但它是一张照片,不是一条实时流。产品在往前走,简报不动。等到 PRD 出现,简报里有一半还是团队当初的第一直觉,而没人会回头去标:哪一半。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">它和自己孕育出的 PRD 脱了节</h3>
            <p>简报本该是 PRD 生长的那颗种子。可一旦 PRD 接管,两者就开始漂移:某个范围决策在 spec 里改了,简报还写着旧的;新同事读了简报,建起错误的心智模型,直到 review 才发现。没有任何东西把这一页和它之后发生的事连起来。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">它的待解问题从不出声地关闭</h3>
            <p>待解问题会被回答 —— 在某条 Slack 线程里、在某人脑子里、在一场没人记纪要的会上。决策发生了,简报却从没听说。于是这份本该承载早期思考的文档,悄悄不再反映团队真正定下的东西,也就不再值得打开。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份简报,<em>让它活起来。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">简报是结构化的,不只是一段散文</h3>
            <p>在 Draftlize 里,简报不是一张白纸 —— 它就是同样的七个板块,以带类型的卡片存在:问题、目标用户、为何是现在、初步方案、成功指标、范围与非目标、待解问题。每一块都有可寻址的 ID,能被 PRD 或线程引用,于是简报始终是那扇前门,而不是沦为一件遗物。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">简报与下游 PRD 连成一体</h3>
            <p>一个待解问题升级成决策;决策喂给 PRD;PRD 又引用简报。它们是一张连通的图,不是三份失联的文档。改动一个范围决策,凡是建立在它之上的卡片 —— 无论在简报里,还是在它下游的 <a href="/zh/prd-template" style={{ color: 'var(--amber)' }}>PRD 模板</a>里 —— 都会自动标成 stale,就像构建系统会让某个被改文件的所有下游全部失效。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 既能读它、也能写它</h3>
            <p>Claude Code 或 Cursor 通过 MCP,在起草 PRD 之前先读简报,并把已解决的问题按同样的结构写回去。简报不再是一份人在 kickoff 时随手扫一眼的文档,而成了 agent 每一轮都会读的上下文 —— 于是这一页始终是一页,而且一直成立。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          简报的全部价值,在于它短、它早。简报的全部麻烦,也在于又短又早的文档,总是第一个过时。
        </blockquote>
        <cite>保留这一页纸。让底座替你保持它最新。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是产品简报（brief）？', a: '产品简报是在写完整 PRD 之前的一页纸——问题、目标用户、目标、大致范围、关键风险。它是团队能买到的最便宜的对齐，因为它短到一眼读完、也早到还能在投入详细 spec 之前改。' },
            { q: '产品简报和 PRD 有什么区别？', a: '简报是一页纸、先来：在工作被拆细之前，让所有人对问题和这次押注达成一致。PRD 后来、且深入：需求、行为、细节。简报为 PRD 赢得资格——如果那一页说服不了任何人，这个想法就还没到写完整文档的时候。' },
            { q: '产品简报应该包含什么？', a: '通常七部分：一句话概述、问题、目标用户、目标或成功指标、拟采取的方案、范围与非目标、以及待解问题或风险。保持一页——一份要滚动才能读完的简报，只是换了个小名牌的 PRD。' },
            { q: '怎么让简报不过时？', a: '简报通常只在写完那一周成立：PRD 会长过它、决策会和它矛盾。Draftlize 让简报保持一页，但把里面的决策变成和下游 PRD 相连的卡片——于是一个范围决策一变，简报和建立在它之上的一切都自动标成 stale，而不是悄悄变错。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">开始你的产品简报,<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账户送 $5</span>
          <span>用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把上面的模板拿走,或者新建一个项目,让 agent 从你一段话的想法里起草出简报 —— 再把它带进 PRD,替你把每一个决策、以及一切依赖它的东西,持续保持最新。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
      <TemplateCta template="product-brief" locale="zh" />
    </MarketingShell>
  );
}
